import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Alert, Button } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { user, loading, permissions, getIdToken } = useAuth();
  const location = useLocation();
  const [resending, setResending] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const config = getConfig();

  const handleResendEmail = async () => {
    try {
      setResending(true);
      setResendMessage(null);

      const token = await getIdToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch(
        `${config.apiBaseUrl}/api/v2/customers/resend-verification-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || 'Failed to resend verification email'
        );
      }

      setResendMessage({
        type: 'success',
        message: 'Verification email sent! Check your inbox.',
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setResendMessage(null);
      }, 5000);
    } catch (err: any) {
      setResendMessage({
        type: 'error',
        message: err.message || 'Failed to resend verification email. Please try again.',
      });
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if email is verified
  if (!permissions?.emailVerified) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 500 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Email Verification Required</strong>
            <p>
              Please verify your email address to access the platform. Check your inbox for the
              verification link.
            </p>
            <p>If you didn't receive an email, please contact support.</p>
          </Alert>

          {resendMessage && (
            <Alert severity={resendMessage.type} sx={{ mb: 2 }}>
              {resendMessage.message}
            </Alert>
          )}

          <Button
            variant="contained"
            onClick={handleResendEmail}
            disabled={resending}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              mt: 2,
            }}
          >
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </Button>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
